import { FilterExpression, like, Pageable } from '@infuse/api/common';
import { ChargingStation as ApiChargingStation, searchChargingStations } from '@infuse/api/cpms/operator';
import { apiResultToDomain } from '@infuse/cpms/charging-station/store';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { computed, ref } from 'vue';

const createSearchFilter = (searchQuery: string | undefined): FilterExpression | undefined => {
  if (!searchQuery || searchQuery === '') {
    return;
  }

  const filterOptions = searchQuery
    .toLowerCase()
    .replace('%', '\\%')
    .split(' ')
    .filter((part) => !!part)
    .map((part) => `%${part}%`)
    .map((part) => new FilterExpression().and(like('lower(id)', part).or(like('lower(customIdentifier)', part))));

  if (filterOptions.length === 0) {
    return;
  }
  return filterOptions.reduce((acc, cv) => acc.and(cv));
};

export const useChargingStationsNotInOrganisation = () => {
  const organisationId = ref<string | undefined>();
  const searchQuery = ref<string | undefined>();
  const pageSize = 20;

  const query = useInfiniteQuery<Pageable<ApiChargingStation>>({
    initialPageParam: 0,
    enabled: () => Boolean(organisationId.value),
    queryKey: ['charging-stations', 'not-in-organisation', organisationId, searchQuery],
    queryFn: async ({ pageParam = 0 }) => {
      let filter = new FilterExpression();

      const searchFilter = createSearchFilter(searchQuery.value);
      if (searchFilter) {
        filter = filter.and(searchFilter);
      }

      return await searchChargingStations({
        sort: [{ key: 'id' }],
        size: pageSize,
        filter: filter.operations?.length ? filter : undefined,
        page: pageParam as number,
        includeTotal: false
      });
    },
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.pageable.pageNumber + 1)
  });

  const chargingStations = computed(
    () => query.data.value?.pages.flatMap((page) => page.content.map(apiResultToDomain)) || []
  );

  const hasMore = computed(() => query.hasNextPage.value);

  const loadMore = () => {
    if (hasMore.value) {
      query.fetchNextPage();
    }
  };

  return {
    ...query,
    chargingStations,
    hasMore,
    organisationId,
    searchQuery,
    loadMore
  };
};
